import { DagilimCubugu } from "./grafik/DagilimCubugu";
import type { DagilimDilimi, GrafikTonu } from "./grafik/tipler";

/**
 * KONU DURUMU DAĞILIMI — dalga 3 (F-TPC-01/01b/04)
 *
 * Dokuz durumun HER BİRİ ayrı dilim: "bitti" diye ara durumlar
 * birleştirilmiyor (Codex analizi §3). Sıfır adetli durum tabloda yer alır;
 * payı bileşen hesaplar.
 *
 * Etiketler ÇAĞIRANDAN geliyor (`DURUM_ADI`): panel ve öğrenci uygulaması
 * kendi sözlüğünü zaten taşıyor; burada üçüncü bir kopya yazılsaydı biri
 * değişince öteki eskirdi. Sıra da o sözlüğün anahtar sırası.
 *
 * ARŞİVLİ KONU SAYILMIYOR: müfredattan çıkarılmış konu geçmiş için listede
 * duruyor ama "şu an nerede" sorusunun cevabı değil. Kapsam metinle söyleniyor.
 */
export interface DagilimKonusu {
  stage: string;
  arsivli?: boolean;
}

const DURUM_TONU: Readonly<Record<string, GrafikTonu>> = {
  NOT_STARTED: "notr",
  PLANNED: "notr",
  IN_PROGRESS: "marka",
  FIRST_PASS_DONE: "marka",
  PRACTICING: "marka",
  NEEDS_REINFORCEMENT: "uyari",
  NEEDS_REVIEW: "uyari",
  VERIFIED_BY_EXAM: "onay",
  MASTERED: "onay",
};

/** Saf: etiket sözlüğünün sırasıyla dilimler; arşivli konu dışarıda. */
export function konuDurumDilimleri(
  konular: readonly DagilimKonusu[],
  etiketler: Readonly<Record<string, string>>,
): { dilimler: DagilimDilimi[]; sayilan: number; arsivli: number; tamamlanan: number } {
  const sayac = new Map<string, number>();
  let arsivli = 0;
  for (const k of konular) {
    if (k.arsivli) {
      arsivli += 1;
      continue;
    }
    sayac.set(k.stage, (sayac.get(k.stage) ?? 0) + 1);
  }
  const dilimler = Object.entries(etiketler).map(([durum, etiket]) => ({
    etiket,
    deger: sayac.get(durum) ?? 0,
    ton: DURUM_TONU[durum] ?? "notr",
  }));
  // F-TPC-04 ile AYNI tanım: yalnızca "tamamlandı" ve "denemede doğrulandı".
  const tamamlanan = (sayac.get("MASTERED") ?? 0) + (sayac.get("VERIFIED_BY_EXAM") ?? 0);
  return { dilimler, sayilan: konular.length - arsivli, arsivli, tamamlanan };
}

export function KonuDurumDagilimi({
  konular,
  etiketler,
  kapsamMetni,
  baslik = "Konu durumu dağılımı",
}: {
  konular: readonly DagilimKonusu[];
  etiketler: Readonly<Record<string, string>>;
  /** Hangi konuların sayıldığı, ör. "Bütün dersler" ya da "TYT Matematik". */
  kapsamMetni: string;
  baslik?: string;
}) {
  const { dilimler, sayilan, arsivli, tamamlanan } = konuDurumDilimleri(konular, etiketler);
  if (sayilan === 0) return null;
  return (
    <section aria-label={baslik} className="flex flex-col gap-1">
      <DagilimCubugu
        baslik={baslik}
        dilimler={dilimler}
        ozet={`${kapsamMetni}: tamamlanan ya da denemede doğrulanan konu ${tamamlanan} / ${sayilan}.`}
      />
      <p className="text-notr-600 text-xs">
        {kapsamMetni} · {sayilan} konu
        {arsivli > 0 ? ` · müfredattan çıkarılmış ${arsivli} konu sayılmadı` : ""}
      </p>
    </section>
  );
}
